import React, { useState, useRef, useEffect, useMemo, useCallback, Dispatch, SetStateAction, }
  from 'react';
import {
  Stage,
  Layer,
  Rect,
  Line,
  Text as KonvaText,
  Circle,
  Transformer,
  Group,
} from 'react-konva';
import Konva from 'konva';
import styles from './PatternStudio.module.scss';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { storage } from '../../utils/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Ruler } from './components/Ruler';
import { FaHandPaper, FaPenNib, FaRegCircle } from 'react-icons/fa';
import { MdCropSquare } from 'react-icons/md';
import { CiPen } from 'react-icons/ci';
import { FormattedMessage, useIntl } from 'react-intl';
import BezierPenLayer from './components/BeizurePenLayer';
import { useBezierTool } from './utils/useBeizure';
import type { BezierPenShape } from './types/beizure.types';

// Shapes and bezier shapes are managed by the history hook.
// Discrete commits are stored as immutable snapshots for undo/redo.
import {
  useCanvasHistory,
  type Shape,
  type BezierShapeRecord,
} from './hooks/useCanvasHistory';

const PPI = 96;

export const UNIT_RATIOS: Record<UnitType, number> = {
  px: 1,
  cm: PPI / 2.54,
  in: PPI,
};

export type UnitType = 'px' | 'cm' | 'in';

// ─── Local types ─────────────────────────────────────────────────────────────

type HoveredHandle = {
  shapeId: string;
  kind: 'corner' | 'edge' | 'control' | 'vertex';
  index: number;
} | null;

type OverlayHandle = {
  shapeId: string;
  kind: 'corner' | 'edge';
  index: number;
};

type DraggingShapeOffset = { id: string; x: number; y: number };

// BezierPenShape from the project — cast it to BezierShapeRecord for the history
// hook, which only needs id + the fields we snapshot. We keep BezierPenShape for
// all live canvas operations so nothing else breaks.
type BPS = BezierPenShape;

function toBSR(b: BPS): BezierShapeRecord {
  return b as unknown as BezierShapeRecord;
}
function fromBSR(b: BezierShapeRecord): BPS {
  return b as unknown as BPS;
}

// ─── Component ───────────────────────────────────────────────────────────────

const PatternStudio: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const auth = useSelector((state: any) => state.auth);
  const intl = useIntl();

  // Paper / canvas size (A4 landscape default)
  const [paperSize, setPaperSize] = useState<{ w: number; h: number }>({ w: 1123, h: 794 });

  const {
    shapes,
    bezierShapes: bezierShapesRaw,
    setShapesLive,
    setBezierShapesLive,
    commitState,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useCanvasHistory([], []);

  const setShapes: Dispatch<SetStateAction<Shape[]>> = setShapesLive;
  const bezierShapes = bezierShapesRaw.map(fromBSR);
  const setBezierShapes = useCallback(
    (arg: BPS[] | ((prev: BPS[]) => BPS[])) => {
      setBezierShapesLive((prev) => {
        const prevTyped = prev.map(fromBSR);
        const next = typeof arg === 'function' ? arg(prevTyped) : arg;
        return next.map(toBSR);
      });
    },
    [setBezierShapesLive],
  );

  // Commit guard: prevent duplicate snapshot commits during rapid updates.
  const commitOnce = useRef(false);

  const commitWithBezier = useCallback(
    (nextShapes: Shape[], nextBezier: BPS[]) => {
      commitState(nextShapes, nextBezier.map(toBSR));
    },
    [commitState],
  );

  const safeCommit = useCallback(
    (nextShapes: Shape[], nextBezier: BPS[]) => {
      if (commitOnce.current) return;

      commitOnce.current = true;

      commitWithBezier(nextShapes, nextBezier);

      requestAnimationFrame(() => {
        commitOnce.current = false;
      });
    },
    [commitWithBezier],
  );

  // UI state (not part of canvas history)
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedHandle, setSelectedHandle] = useState<OverlayHandle | null>(null);
  const [draggingOverlayHandle, setDraggingOverlayHandle] = useState<OverlayHandle | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [tool, setTool] = useState<string>('select');
  const [isGridVisible, setIsGridVisible] = useState<boolean>(true);
  const [snapEnabled, setSnapEnabled] = useState<boolean>(false);
  const [unit, setUnit] = useState<UnitType>('cm');
  const [draggingShapeOffset, setDraggingShapeOffset] = useState<DraggingShapeOffset | null>(null);
  const [selectedBezierPenId, setSelectedBezierPenId] = useState<string | null>(null);
  const [stagePos, setStagePos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState<number>(1);
  const [dimensions, setDimensions] = useState<{ w: number; h: number }>({ w: 800, h: 600 });
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [selectedVertexIndex, setSelectedVertexIndex] = useState<number | null>(null);
  const [, setHoveredHandle] = useState<HoveredHandle>(null);

  // Refs
  const stageContainerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<Konva.Stage>(null);
  const layerRef = useRef<Konva.Layer>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const overlayRef = useRef<Konva.Layer>(null);
  const isDrawing = useRef<boolean>(false);
  const transformBeforeRef = useRef<Konva.Transform | null>(null);
  const pendingDragRef = useRef<{ id: string; shape: Shape } | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const prevToolRef = useRef<string | null>(null);
  const isMiddlePanningRef = useRef<boolean>(false);
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const panOriginRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const didModifyRef = useRef(false);

  // ── Grid spacing ──────────────────────────────────────────────────────────
  const getGridSpacing = useCallback((): number => {
    if (unit === 'px') return 50;
    return UNIT_RATIOS[unit];
  }, [unit]);

  const getGridSpacingRef = useRef<() => number>(getGridSpacing);
  useEffect(() => {
    getGridSpacingRef.current = getGridSpacing;
  }, [getGridSpacing]);

  const snapToGrid = useCallback(
    (val: number): number => {
      if (!snapEnabled) return val;
      const spacing = getGridSpacing();
      return Math.round(val / spacing) * spacing;
    },
    [snapEnabled, getGridSpacing],
  );

  // ── Snap ref for useBezierTool (stable across renders) ───────────────────
  const snapToGridFn = useCallback(
    (val: number): number => {
      if (!snapEnabled) return val;
      const spacing = getGridSpacingRef.current?.() ?? 50;
      return Math.round(val / spacing) * spacing;
    },
    [snapEnabled],
  );

  // ── Bézier Pen tool ───────────────────────────────────────────────────────
  const {
    isDrawing: isBezierPenDrawing,
    handleMouseDown: bezierPenMouseDown,
    handleMouseMove: bezierPenMouseMove,
    handleMouseUp: bezierPenMouseUp,
    handleDblClick: bezierPenDblClick,
    finishPath: bezierPenFinish,
    cancelPath: bezierPenCancel,
  } = useBezierTool({
    onShapeUpdate: (shape: BPS) => {
      setBezierShapes((prev) => {
        const idx = prev.findIndex((s) => s.id === shape.id);
        if (idx === -1) return [...prev, shape];
        const next = prev.slice();
        next[idx] = shape;
        return next;
      });
      setSelectedBezierPenId(shape.id);
    },
    onShapeCommit: (shape: BPS) => {
      setBezierShapes((prev) => {
        const idx = prev.findIndex((s) => s.id === shape.id);

        return idx === -1
          ? [...prev, shape]
          : prev.map((s) => (s.id === shape.id ? shape : s));
      });

      const next =
        bezierShapes.findIndex((s) => s.id === shape.id) === -1
          ? [...bezierShapes, shape]
          : bezierShapes.map((s) =>
            s.id === shape.id ? shape : s
          );

      safeCommit(shapes, next);
    },
    onShapeCancel: (shapeId: string) => {
      setBezierShapes((prev) => prev.filter((s) => s.id !== shapeId));
      setSelectedBezierPenId(null);
      setTool('select');
    },
    snapToGrid: snapToGridFn,
  });

  // ── Outside-click / keyboard / global mouseup ────────────────────────────
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    const handleGlobalMouseUp = () => {
      if (isMiddlePanningRef.current) restoreAfterMiddlePan();
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('keydown', handleEsc);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('keydown', handleEsc);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
    // restoreAfterMiddlePan is defined later but only uses refs — stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Container resize observer ─────────────────────────────────────────────
  useEffect(() => {
    const checkSize = () => {
      if (stageContainerRef.current) {
        setDimensions({
          w: stageContainerRef.current.offsetWidth,
          h: stageContainerRef.current.offsetHeight,
        });
      }
    };
    window.addEventListener('resize', checkSize);
    checkSize();
    setTimeout(checkSize, 100);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  // ── Global error diagnostics ──────────────────────────────────────────────
  useEffect(() => {
    const onError = (ev: ErrorEvent) => {
      console.error('[PS] window.error', ev.message, ev.error, {
        selectedId,
        selectedHandle,
        shapesLength: shapes.length,
      });
    };
    const onRejection = (ev: PromiseRejectionEvent) => {
      console.error('[PS] unhandledrejection', ev.reason, {
        selectedId,
        selectedHandle,
        shapesLength: shapes.length,
      });
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, [shapes, selectedId, selectedHandle]);

  // ── Load saved pattern ────────────────────────────────────────────────────
  useEffect(() => {
    if (!id) return;
    const fetchPattern = async () => {
      try {
        const { data } = await axios.get(`/api/patterns/id/${id}`);
        if (data.designData) {
          const loadedShapes: Shape[] = data.designData.shapes ?? [];
          const loadedBezier: BPS[] = data.designData.bezierShapes ?? [];
          // Seed live state
          setShapes(loadedShapes);
          setBezierShapes(loadedBezier);
          // Seed history so loaded state is the initial undo base-point
          safeCommit(loadedShapes, loadedBezier);
          if (data.designData.canvas) {
            const c = data.designData.canvas;
            if (c.w && c.h) setPaperSize({ w: c.w, h: c.h });
            if (c.unit) setUnit(c.unit as UnitType);
          }
        }
      } catch (error) {
        console.error('Failed to load pattern', error);
      }
    };
    fetchPattern();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // ── Transformer sync ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!transformerRef.current || !stageRef.current) return;
    if (!selectedId) {
      transformerRef.current.nodes([]);
      return;
    }
    const selectedShape = shapes.find((s) => s.id === selectedId);
    if (selectedShape?.type === 'bezier') {
      transformerRef.current.nodes([]);
      return;
    }
    const node = stageRef.current.findOne(`#${selectedId}`);
    if (node) {
      transformerRef.current.nodes([node]);
      transformerRef.current.getLayer()?.batchDraw();
    } else {
      transformerRef.current.nodes([]);
    }
  }, [selectedId, shapes]);

  // ── Grid lines (memoised) ─────────────────────────────────────────────────
  const gridLines = useMemo<{ v: number[]; h: number[] }>(() => {
    if (!isGridVisible) return { v: [], h: [] };
    const spacing = getGridSpacing();
    const startX = Math.floor((-stagePos.x / stageScale) / spacing) * spacing;
    const endX = Math.floor(((-stagePos.x + dimensions.w) / stageScale) / spacing) * spacing;
    const startY = Math.floor((-stagePos.y / stageScale) / spacing) * spacing;
    const endY = Math.floor(((-stagePos.y + dimensions.h) / stageScale) / spacing) * spacing;
    const buf = 2 * spacing;
    const v: number[] = [];
    const h: number[] = [];
    for (let x = startX - buf; x <= endX + buf; x += spacing) v.push(x);
    for (let y = startY - buf; y <= endY + buf; y += spacing) h.push(y);
    return { v, h };
  }, [stagePos, stageScale, dimensions, isGridVisible, getGridSpacing]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    const designData = {
      shapes,
      bezierShapes,
      canvas: { w: paperSize.w, h: paperSize.h, unit },
    };
    let previewUrl = '';
    if (stageRef.current) {
      const prevPos = stageRef.current.position();
      const prevScale = { x: stageRef.current.scaleX(), y: stageRef.current.scaleY() };
      stageRef.current.position({ x: 0, y: 0 });
      stageRef.current.scale({ x: 1, y: 1 });
      stageRef.current.batchDraw();
      previewUrl = stageRef.current.toDataURL({
        pixelRatio: 0.5, x: 0, y: 0, width: paperSize.w, height: paperSize.h,
      });
      stageRef.current.position(prevPos);
      stageRef.current.scale(prevScale);
      stageRef.current.batchDraw();
    }
    const payload = {
      name: `Pattern Project ${new Date().toLocaleDateString()}`,
      price: 0,
      designData,
      isEditable: true,
      patternFile: 'editable-layout',
      isPublic: false,
      previewUrl,
    };
    const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': auth?.token } };
    try {
      if (id) {
        await axios.put(`/api/patterns/${id}`, payload, config);
        toast.success('Saved successfully!');
      } else {
        const { data } = await axios.post('/api/patterns', payload, config);
        navigate(`/designer/${data._id}`);
        toast.success('Created and Saved!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Error saving pattern');
    }
  }, [shapes, bezierShapes, paperSize, unit, auth, id, navigate]);

  // ── Export PDF ────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!stageRef.current) return;
    const currentSelection = selectedId;
    const wasGridVisible = isGridVisible;
    setSelectedId(null);
    setIsGridVisible(false);

    setTimeout(async () => {
      if (!stageRef.current) return;
      const prevPos = stageRef.current.position();
      const prevScale = { x: stageRef.current.scaleX(), y: stageRef.current.scaleY() };
      try {
        stageRef.current.position({ x: 0, y: 0 });
        stageRef.current.scale({ x: 1, y: 1 });
        stageRef.current.batchDraw();
        const dataUri = stageRef.current.toDataURL({
          pixelRatio: 2, x: 0, y: 0, width: paperSize.w, height: paperSize.h,
        });
        stageRef.current.position(prevPos);
        stageRef.current.scale(prevScale);
        stageRef.current.batchDraw();

        const pdf = new jsPDF('l', 'px', [paperSize.w, paperSize.h]);
        pdf.addImage(dataUri, 'PNG', 0, 0, paperSize.w, paperSize.h);

        if (id && auth?.token) {
          const pdfBlob = pdf.output('blob');
          const fileName = `patterns/${id}/${Date.now()}.pdf`;
          const storageRef = ref(storage, fileName);
          await uploadBytes(storageRef, pdfBlob);
          const downloadURL = await getDownloadURL(storageRef);
          const config = { headers: { 'Content-Type': 'application/json', 'x-auth-token': auth?.token } };
          await axios.put(`/api/patterns/${id}`, { patternFile: downloadURL }, config);
          toast.success('Saved PDF to cloud!');
        } else {
          pdf.save('pattern.pdf');
          toast.success('PDF downloaded');
        }
      } catch (err) {
        console.error('Export error', err);
        toast.error('Export failed');
        if (stageRef.current) {
          stageRef.current.position(prevPos);
          stageRef.current.scale(prevScale);
          stageRef.current.batchDraw();
        }
      } finally {
        setIsGridVisible(wasGridVisible);
        setSelectedId(currentSelection);
      }
    }, 100);
  };

  // ── Unit helpers ──────────────────────────────────────────────────────────
  const toUnit = (px: number): number => px / UNIT_RATIOS[unit];
  const fromUnit = useCallback(
    (val: number): number => val * UNIT_RATIOS[unit],
    [unit],
  );

  const formatMeasurement = (px: number): string => {
    if (px == null || Number.isNaN(px)) return '';
    const converted = toUnit(px);
    if (unit === 'px') return `${Math.round(converted)} px`;
    return `${(Math.round(converted * 10) / 10).toFixed(1)} ${unit}`;
  };

  // ── Shape geometry helpers ────────────────────────────────────────────────
  const getShapeBounds = (shape: Shape): { x: number; y: number; w: number; h: number } => {
    if (!shape) return { x: 0, y: 0, w: 0, h: 0 };
    if (shape.type === 'rect') {
      const w = shape.width ?? 0;
      const h = shape.height ?? 0;
      return { x: Math.min(shape.x, shape.x + w), y: Math.min(shape.y, shape.y + h), w: Math.abs(w), h: Math.abs(h) };
    }
    if (shape.type === 'circle') {
      const r = shape.radius ?? 0;
      return { x: shape.x - r, y: shape.y - r, w: r * 2, h: r * 2 };
    }
    if (shape.points && shape.points.length >= 2) {
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < shape.points.length; i += 2) {
        const px = shape.points[i];
        const py = shape.points[i + 1];
        if (px < minX) minX = px;
        if (py < minY) minY = py;
        if (px > maxX) maxX = px;
        if (py > maxY) maxY = py;
      }
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }
    return { x: shape.x ?? 0, y: shape.y ?? 0, w: 20, h: 20 };
  };

  const getShapeLength = (shape: Shape): number => {
    if (!shape) return 0;
    if (shape.type === 'line' && shape.points && shape.points.length >= 4) {
      const dx = shape.points[2] - shape.points[0];
      const dy = shape.points[3] - shape.points[1];
      return Math.sqrt(dx * dx + dy * dy);
    }
    if ((shape.type === 'pen' || shape.type === 'bezier') && shape.points && shape.points.length >= 4) {
      let len = 0;
      for (let i = 0; i < shape.points.length - 2; i += 2) {
        const dx = shape.points[i + 2] - shape.points[i];
        const dy = shape.points[i + 3] - shape.points[i + 1];
        len += Math.sqrt(dx * dx + dy * dy);
      }
      return len;
    }
    if (shape.type === 'circle' && shape.radius) return 2 * Math.PI * shape.radius;
    if (shape.type === 'rect' && shape.width != null && shape.height != null)
      return 2 * (Math.abs(shape.width) + Math.abs(shape.height));
    return 0;
  };

  // ── Shape state helpers ───────────────────────────────────────────────────
  const handleShapeUpdate = useCallback(
    (newAttrs: Shape) => {
      const next = shapes.map((s) => (s.id === newAttrs.id ? newAttrs : s));
      setShapes(next);
      safeCommit(next, bezierShapes);
    },
    [shapes, bezierShapes, setShapes, safeCommit],
  );

  const updateSelectedProperty = useCallback(
    (key: keyof Shape, value: Shape[keyof Shape]) => {
      if (!selectedId) return;
      const next = shapes.map((s) => (s.id === selectedId ? { ...s, [key]: value } : s));
      setShapes(next);
      safeCommit(next, bezierShapes);
    },
    [selectedId, shapes, bezierShapes, setShapes, safeCommit],
  );

  const updateLineLength = useCallback(
    (newLength: number) => {
      const selectedShape = shapes.find((s) => s.id === selectedId);
      if (!selectedShape || selectedShape.type !== 'line') return;
      if (!selectedShape.points || selectedShape.points.length < 4) return;
      const p = selectedShape.points;
      const angle = Math.atan2(p[3] - p[1], p[2] - p[0]);
      const lenPx = fromUnit(newLength);
      handleShapeUpdate({
        ...selectedShape,
        points: [p[0], p[1], p[0] + lenPx * Math.cos(angle), p[1] + lenPx * Math.sin(angle)],
      });
    },
    [selectedId, shapes, fromUnit, handleShapeUpdate], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const deleteSelected = useCallback(() => {
    if (!selectedId) return;
    const shape = shapes.find((s) => s.id === selectedId);

    // Vertex deletion for quad / pen
    if (
      selectedVertexIndex !== null &&
      shape &&
      (shape.type === 'quad' || shape.type === 'pen')
    ) {
      const pts = shape.points ?? [];
      if (pts.length / 2 > 1) {
        const newPts = pts.filter(
          (_, i) => i !== selectedVertexIndex * 2 && i !== selectedVertexIndex * 2 + 1,
        );
        const next = shapes.map((s) => (s.id === selectedId ? { ...s, points: newPts } : s));
        setShapes(next);
        safeCommit(next, bezierShapes);
        setSelectedVertexIndex(null);
        return;
      }
    }

    const next = shapes.filter((s) => s.id !== selectedId);
    setShapes(next);
    safeCommit(next, bezierShapes);
    setSelectedId(null);
    setSelectedVertexIndex(null);
  }, [selectedId, selectedVertexIndex, shapes, bezierShapes, setShapes, safeCommit]);

  // ── RAF-batched drag helpers ──────────────────────────────────────────────
  const flushPendingDrag = useCallback((shapeId?: string) => {
    const p = pendingDragRef.current;
    if (p && (!shapeId || p.id === shapeId)) {
      pendingDragRef.current = null;
      setShapes((prev) => {
        const next = prev.slice();
        const idx = next.findIndex((s) => s.id === p.id);
        if (idx === -1) return prev;
        next[idx] = p.shape;
        return next;
      });
    }
    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }
  }, [setShapes]);

  const handlePointDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, index: number, shapeId: string) => {
      try {
        const stage = e.target.getStage();
        const pointer = stage?.getPointerPosition();
        if (!stage || !pointer) return;

        const transform = stage.getAbsoluteTransform().copy().invert();
        const pos = transform.point(pointer);
        let x = pos.x;
        let y = pos.y;
        if (snapEnabled) { x = snapToGrid(x); y = snapToGrid(y); }

        const si = shapes.findIndex((sh) => sh.id === shapeId);
        if (si === -1) return;
        const shape = shapes[si];
        if (!shape.points) return;

        const newPoints = [...shape.points];
        newPoints[index * 2] = x;
        newPoints[index * 2 + 1] = y;

        if (!Number.isFinite(x) || !Number.isFinite(y)) return;
        if (newPoints.some((p) => !Number.isFinite(p))) return;

        const updatedShape: Shape = { ...shape, points: newPoints };
        pendingDragRef.current = { id: shapeId, shape: updatedShape };

        if (dragRafRef.current === null) {
          const captured = pendingDragRef.current;
          dragRafRef.current = requestAnimationFrame(() => {
            if (!captured) { dragRafRef.current = null; return; }
            setShapes((prev) => {
              const next = prev.slice();
              const idx = next.findIndex((s) => s.id === captured.id);
              if (idx === -1) return prev;
              next[idx] = captured.shape;
              return next;
            });
            if (pendingDragRef.current === captured) pendingDragRef.current = null;
            dragRafRef.current = null;
          });
        }
      } catch (err) {
        console.error('[PS] handlePointDragMove', err, { index, shapeId });
      }
    },
    [shapes, snapEnabled, snapToGrid, setShapes],
  );

  const handleRectHandleDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, handleKind: 'corner' | 'edge', idx: number, shapeId: string) => {
      try {
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (!pos) return;

        const transform = stage!.getAbsoluteTransform().copy();
        transform.invert();
        const point = transform.point(pos);
        let nx = point.x;
        let ny = point.y;
        if (snapEnabled) { nx = snapToGrid(nx); ny = snapToGrid(ny); }

        const si = shapes.findIndex((sh) => sh.id === shapeId);
        if (si === -1) return;
        const shape = shapes[si];

        let updatedShape: Shape | null = null;

        if (shape.type === 'quad') {
          const pts = shape.points ? [...shape.points] : [0, 0, 0, 0, 0, 0, 0, 0];
          if (handleKind === 'corner') {
            pts[idx * 2] = nx;
            pts[idx * 2 + 1] = ny;
          } else {
            const a = idx;
            const b = (idx + 1) % 4;
            const dx = nx - (pts[a * 2] + pts[b * 2]) / 2;
            const dy = ny - (pts[a * 2 + 1] + pts[b * 2 + 1]) / 2;
            pts[a * 2] += dx; pts[a * 2 + 1] += dy;
            pts[b * 2] += dx; pts[b * 2 + 1] += dy;
          }
          updatedShape = { ...shape, points: pts };
        } else {
          const bounds = getShapeBounds(shape);
          const left = bounds.x;
          const top = bounds.y;
          const right = bounds.x + bounds.w;
          const bottom = bounds.y + bounds.h;
          const minSize = 5;
          let newX = left, newY = top, newW = bounds.w, newH = bounds.h;

          if (handleKind === 'corner') {
            if (idx === 0) {
              newX = Math.min(nx, right - minSize); newY = Math.min(ny, bottom - minSize);
              newW = Math.max(minSize, right - newX); newH = Math.max(minSize, bottom - newY);
            } else if (idx === 1) {
              newY = Math.min(ny, bottom - minSize);
              newW = Math.max(minSize, nx - left); newH = Math.max(minSize, bottom - newY);
            } else if (idx === 2) {
              newW = Math.max(minSize, nx - left); newH = Math.max(minSize, ny - top);
            } else if (idx === 3) {
              newX = Math.min(nx, right - minSize);
              newW = Math.max(minSize, right - newX); newH = Math.max(minSize, ny - top);
            }
          } else {
            if (idx === 0) { newY = Math.min(ny, bottom - minSize); newH = Math.max(minSize, bottom - newY); }
            else if (idx === 1) { newW = Math.max(minSize, nx - left); }
            else if (idx === 2) { newH = Math.max(minSize, ny - top); }
            else if (idx === 3) { newX = Math.min(nx, right - minSize); newW = Math.max(minSize, right - newX); }
          }
          updatedShape = { ...shape, x: newX, y: newY, width: newW, height: newH };
        }

        if (!updatedShape) return;

        // Imperative canvas update for smooth drag performance
        try {
          const stg = stageRef.current;
          if (stg) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const groupNode = stg.findOne(`#${shapeId}`) as any;
            if (groupNode) {
              if (updatedShape.type === 'quad' && Array.isArray(updatedShape.points)) {
                const lineNode = groupNode.findOne('Line');
                if (lineNode && !updatedShape.points.some((p) => !Number.isFinite(p))) {
                  lineNode.points(updatedShape.points);
                  lineNode.getLayer()?.batchDraw();
                }
              } else {
                const rectNode = groupNode.findOne('Rect');
                const { x: ax, y: ay, width: aw, height: ah } = updatedShape;
                if (rectNode && [ax, ay, aw, ah].every((v) => Number.isFinite(v ?? NaN))) {
                  rectNode.setAttrs({ x: ax, y: ay, width: aw, height: ah });
                  rectNode.getLayer()?.batchDraw();
                }
              }
            }
          }
        } catch (err) {
          console.warn('[PS] imperative update failed', err);
        }

        const isValid =
          updatedShape.type === 'quad'
            ? Array.isArray(updatedShape.points) && updatedShape.points.every((n) => Number.isFinite(n))
            : (['x', 'y', 'width', 'height'] as const).every((k) =>
              Number.isFinite(updatedShape[k] ?? 0)
            );

        if (!isValid) return;

        const isActiveHandle =
          selectedHandle?.shapeId === shapeId &&
          selectedHandle.kind === handleKind &&
          selectedHandle.index === idx;

        pendingDragRef.current = { id: shapeId, shape: updatedShape };
        const captured = pendingDragRef.current;
        if (dragRafRef.current === null && !isActiveHandle) {
          dragRafRef.current = requestAnimationFrame(() => {
            if (!captured) { dragRafRef.current = null; return; }
            setShapes((prev) => {
              const next = prev.slice();
              const idx2 = next.findIndex((s) => s.id === captured.id);
              if (idx2 === -1) return prev;
              next[idx2] = captured.shape;
              return next;
            });
            if (pendingDragRef.current === captured) pendingDragRef.current = null;
            dragRafRef.current = null;
          });
        }
      } catch (err) {
        console.error('[PS] handleRectHandleDragMove', err, { shapeId, handleKind, idx });
        flushPendingDrag(shapeId);
      }
    },
    [shapes, snapEnabled, snapToGrid, selectedHandle, setShapes, flushPendingDrag], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handlePolygonEdgeDragMove = useCallback(
    (e: Konva.KonvaEventObject<DragEvent>, idx: number, shapeId: string) => {
      try {
        const stage = e.target.getStage();
        const pos = stage?.getPointerPosition();
        if (!pos) return;
        const transform = stage!.getAbsoluteTransform().copy();
        transform.invert();
        const point = transform.point(pos);
        let nx = point.x;
        let ny = point.y;
        if (snapEnabled) { nx = snapToGrid(nx); ny = snapToGrid(ny); }

        const si = shapes.findIndex((sh) => sh.id === shapeId);
        if (si === -1) return;
        const sh = shapes[si];
        if (!sh.points || sh.points.length < 4) return;

        const pts = [...sh.points];
        const n = pts.length / 2;
        const a = idx;
        const b = (idx + 1) % n;
        const dx = nx - (pts[a * 2] + pts[b * 2]) / 2;
        const dy = ny - (pts[a * 2 + 1] + pts[b * 2 + 1]) / 2;
        pts[a * 2] += dx; pts[a * 2 + 1] += dy;
        pts[b * 2] += dx; pts[b * 2 + 1] += dy;

        const updated: Shape = { ...sh, points: pts };
        pendingDragRef.current = { id: shapeId, shape: updated };

        if (dragRafRef.current === null) {
          const captured = pendingDragRef.current;
          dragRafRef.current = requestAnimationFrame(() => {
            if (!captured) { dragRafRef.current = null; return; }
            setShapes((prev) => {
              const next = prev.slice();
              const idx2 = next.findIndex((s) => s.id === captured.id);
              if (idx2 === -1) return prev;
              next[idx2] = captured.shape;
              return next;
            });
            if (pendingDragRef.current === captured) pendingDragRef.current = null;
            dragRafRef.current = null;
          });
        }
      } catch (err) {
        console.error('[PS] handlePolygonEdgeDragMove', err, { idx, shapeId });
      }
    },
    [shapes, snapEnabled, snapToGrid, setShapes],
  );

  // ── convertRectToQuad ─────────────────────────────────────────────────────
  const convertRectToQuad = useCallback(
    (shapeId: string) => {
      setShapes((prev) => {
        const s = prev.slice();
        const si = s.findIndex((sh) => sh.id === shapeId);
        if (si === -1) return prev;
        const sh = s[si];
        if (sh.type === 'rect') {
          const w = sh.width ?? 0;
          const h = sh.height ?? 0;
          s[si] = {
            ...sh,
            type: 'quad',
            points: [sh.x, sh.y, sh.x + w, sh.y, sh.x + w, sh.y + h, sh.x, sh.y + h],
          };
        }
        return s;
      });
    },
    [setShapes],
  );

  // ── insertPointOnEdge ─────────────────────────────────────────────────────
  const insertPointOnEdge = useCallback(
    (shapeId: string, edgeIndex: number, pos: { x: number; y: number }) => {
      const origShape = shapes.find((s) => s.id === shapeId);
      const origN = origShape
        ? origShape.type === 'rect'
          ? 4
          : Math.max(0, Math.floor((origShape.points?.length ?? 0) / 2))
        : 4;

      // Compute the next shapes array outside the setter so we can commit cleanly
      const si = shapes.findIndex((sh) => sh.id === shapeId);
      if (si === -1) return;
      const sh = shapes[si];

      let pts: number[];
      if (sh.type === 'rect') {
        const w = sh.width ?? 0;
        const h = sh.height ?? 0;
        pts = [sh.x, sh.y, sh.x + w, sh.y, sh.x + w, sh.y + h, sh.x, sh.y + h];
      } else if (sh.points && sh.points.length >= 4) {
        pts = sh.points.slice();
      } else {
        return;
      }

      if (!Number.isFinite(pos.x) || !Number.isFinite(pos.y)) return;

      const n = Math.max(2, Math.floor(pts.length / 2));
      const insertAt = ((edgeIndex + 1) % n) * 2;
      pts.splice(insertAt, 0, pos.x, pos.y);

      const next = shapes.slice();
      next[si] = { ...sh, type: 'pen', points: pts, closed: true };

      setShapes(next);
      safeCommit(next, bezierShapes);

      setTimeout(() => {
        const newIndex = (edgeIndex + 1) % Math.max(1, origN);
        setSelectedId(shapeId);
        setSelectedHandle({ shapeId, kind: 'corner', index: newIndex });
        setDraggingOverlayHandle({ shapeId, kind: 'corner', index: newIndex });
      }, 0);
    },
    [shapes, bezierShapes, setShapes, safeCommit],
  );

  // ── Layer panel drag / drop ───────────────────────────────────────────────
  const onLayerDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDraggingIndex(index);
  };
  const onLayerDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };
  const onLayerDrop = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault();
    const from = draggingIndex !== null ? draggingIndex : Number(e.dataTransfer.getData('text/plain'));
    if (from === index) { setDraggingIndex(null); setDragOverIndex(null); return; }
    const next = shapes.slice();
    const [moved] = next.splice(from, 1);
    next.splice(index, 0, moved);
    setShapes(next);
    safeCommit(next, bezierShapes);
    setDraggingIndex(null);
    setDragOverIndex(null);
  };
  const onLayerDragEnd = () => { setDraggingIndex(null); setDragOverIndex(null); };

  // ── Wheel zoom ────────────────────────────────────────────────────────────
  const handleWheel = (e: Konva.KonvaEventObject<WheelEvent>) => {
    e.evt.preventDefault();
    const stage = stageRef.current;
    if (!stage) return;
    const scaleBy = 1.1;
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    if (!pointer) return;
    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };
    let newScale = e.evt.ctrlKey || e.evt.metaKey
      ? oldScale - e.evt.deltaY * 0.01
      : e.evt.deltaY > 0 ? oldScale / scaleBy : oldScale * scaleBy;
    newScale = Math.min(20, Math.max(0.05, newScale));
    setStageScale(newScale);
    setStagePos({
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    });
  };

  const handleStageDragEnd = (e: Konva.KonvaEventObject<DragEvent>) => {
    if (e.target === e.target.getStage()) {
      didModifyRef.current = true;
      setStagePos({ x: e.target.x(), y: e.target.y() });
    }
  };

  // ── Middle-button pan ─────────────────────────────────────────────────────
  const restoreAfterMiddlePan = () => {
    if (!isMiddlePanningRef.current) return;
    isMiddlePanningRef.current = false;
    panStartRef.current = null;
    stageContainerRef.current?.classList.remove(styles.panning);
    stageContainerRef.current?.classList.remove(styles.grabbing);
    if (prevToolRef.current) {
      setTool(prevToolRef.current);
      prevToolRef.current = null;
    }
  };

  // ── Mouse down ────────────────────────────────────────────────────────────
  const handleMouseDown = (e: Konva.KonvaEventObject<MouseEvent>) => {
    const stage = e.target.getStage();
    const pos = stage?.getRelativePointerPosition();
    if (!pos) return;

    // Middle-button pan
    if (e.evt.button === 1) {
      e.evt.preventDefault();
      prevToolRef.current = tool;
      isMiddlePanningRef.current = true;
      panStartRef.current = stage?.getPointerPosition() ?? pos;
      panOriginRef.current = { ...stagePos };
      setTool('hand');
      stageContainerRef.current?.classList.add(styles.panning);
      stageContainerRef.current?.classList.add(styles.grabbing);
      return;
    }

    // Bézier pen
    if (tool === 'bezierPen') {
      setSelectedId(null);
      bezierPenMouseDown(e, stageRef.current);
      return;
    }

    const clickedOnStage = e.target === e.target.getStage();
    if (tool === 'select' || tool === 'hand') {
      if (clickedOnStage) { setSelectedVertexIndex(null); setSelectedId(null); }
      return;
    }

    isDrawing.current = true;
    const newId = `shape-${Date.now()}`;
    const startX = snapToGrid(pos.x);
    const startY = snapToGrid(pos.y);
    let newShape: Shape | null = null;

    if (tool === 'rect') {
      newShape = { id: newId, type: 'rect', x: startX, y: startY, width: 0, height: 0, fill: 'transparent', stroke: '#000000', strokeWidth: 2 };
    } else if (tool === 'circle') {
      newShape = { id: newId, type: 'circle', x: startX, y: startY, radius: 0, fill: 'transparent', stroke: '#000000', strokeWidth: 2 };
    } else if (tool === 'line') {
      newShape = { id: newId, type: 'line', x: 0, y: 0, points: [startX, startY, startX, startY], stroke: '#000000', strokeWidth: 2 };
    } else if (tool === 'pen') {
      newShape = { id: newId, type: 'pen', x: 0, y: 0, points: [pos.x, pos.y], stroke: '#000000', strokeWidth: 2, tension: 0.5, lineCap: 'round', lineJoin: 'round' };
    } else if (tool === 'bezier') {
      newShape = { id: newId, type: 'bezier', x: 0, y: 0, points: [startX, startY, startX, startY, startX, startY, startX, startY], stroke: '#000000', strokeWidth: 2 };
    } else if (tool === 'text') {
      newShape = { id: newId, type: 'text', x: pos.x, y: pos.y, text: 'Text', fontSize: 20, fill: '#000000' };
      isDrawing.current = false;
      setTool('select');
    }

    if (newShape) {
      // Live only — shape is still being drawn, don't commit yet
      setShapes([...shapes, newShape]);
      setSelectedId(newId);
    }
  };

  // ── Mouse move ────────────────────────────────────────────────────────────
  const handleMouseMove = (e: Konva.KonvaEventObject<MouseEvent>) => {
    // Middle-button pan
    if (isMiddlePanningRef.current) {
      const stage = stageRef.current;
      const pointer = stage?.getPointerPosition();
      if (!pointer || !panStartRef.current) return;
      setStagePos({
        x: panOriginRef.current.x + pointer.x - panStartRef.current.x,
        y: panOriginRef.current.y + pointer.y - panStartRef.current.y,
      });
      return;
    }

    if (tool === 'bezierPen' && isBezierPenDrawing) {
      bezierPenMouseMove(e, stageRef.current);
      return;
    }

    if (!isDrawing.current || tool === 'select' || tool === 'text' || tool === 'hand') return;

    const stage = e.target.getStage();
    const pos = stage?.getRelativePointerPosition();
    if (!pos) return;

    const lastShape = shapes[shapes.length - 1];
    if (!lastShape) return;

    const prev = shapes.slice(0, shapes.length - 1);

    if (tool === 'rect') {
      prev.push({ ...lastShape, width: snapToGrid(pos.x) - lastShape.x, height: snapToGrid(pos.y) - lastShape.y });
    } else if (tool === 'circle') {
      const dx = snapToGrid(pos.x) - lastShape.x;
      const dy = snapToGrid(pos.y) - lastShape.y;
      prev.push({ ...lastShape, radius: Math.sqrt(dx * dx + dy * dy) });
    } else if (tool === 'line') {
      const pts = lastShape.points?.slice() ?? [];
      pts[2] = snapToGrid(pos.x);
      pts[3] = snapToGrid(pos.y);
      prev.push({ ...lastShape, points: pts });
    } else if (tool === 'pen') {
      prev.push({ ...lastShape, points: [...(lastShape.points ?? []), pos.x, pos.y] });
    } else if (tool === 'bezier') {
      const p = lastShape.points ?? [];
      const sx = p[0], sy = p[1];
      const ex = snapToGrid(pos.x), ey = snapToGrid(pos.y);
      prev.push({
        ...lastShape,
        points: [sx, sy, sx + (ex - sx) * 0.33, sy + (ey - sy) * 0.33, sx + (ex - sx) * 0.66, sy + (ey - sy) * 0.66, ex, ey],
      });
    }

    setShapes(prev); // live only
  };

  // ── Mouse up ──────────────────────────────────────────────────────────────
  const handleMouseUp = (e?: Konva.KonvaEventObject<MouseEvent>) => {
    if (e?.evt?.button === 1 || isMiddlePanningRef.current) {
      restoreAfterMiddlePan();
      return;
    }
    if (tool === 'bezierPen') {
      bezierPenMouseUp(e, stageRef.current);
      return;
    }

    isDrawing.current = false;
    if (tool !== 'select' && tool !== 'hand' && tool !== 'pen' && tool !== 'bezier') setTool('select');
    if (tool === 'bezier') setTool('select');

    if (!didModifyRef.current) return;
    // ── UNDO: commit completed draw / drag ────────────────────────────────
    safeCommit(shapes, bezierShapes);
  };

  // ── Keyboard shortcuts ────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const ctrl = e.ctrlKey || e.metaKey;

      // Save
      if (ctrl && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        handleSave();
        return;
      }

      // Undo
      if (ctrl && !e.shiftKey && e.key === 'z') {
        e.preventDefault();
        undo();
        return;
      }

      // Redo — Ctrl+Y or Ctrl+Shift+Z
      if (ctrl && (e.key === 'y' || ((e.shiftKey) && (e.key === 'z' || e.key === 'Z')))) {
        e.preventDefault();
        redo();
        return;
      }

      // Bézier pen shortcuts
      if (tool === 'bezierPen') {
        if (e.key === 'Escape') { e.preventDefault(); bezierPenCancel(); return; }
        if (e.key === 'Enter') { e.preventDefault(); bezierPenFinish(false); return; }
      }

      // Delete / Backspace
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        if (selectedId) {
          e.preventDefault();
          deleteSelected();
        } else if (selectedBezierPenId) {
          e.preventDefault();
          const next = bezierShapes.filter((s) => s.id !== selectedBezierPenId);
          setBezierShapes(next);
          safeCommit(shapes, next);
          setSelectedBezierPenId(null);
        }
      }
    };

    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    selectedId, selectedHandle, selectedBezierPenId, tool,
    handleSave, deleteSelected, bezierPenCancel, bezierPenFinish,
    undo, redo, shapes, bezierShapes, setBezierShapes, safeCommit,
  ]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const selectedShape = shapes.find((s) => s.id === selectedId) ?? null;

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className={styles.studioWrapper}>

      {/* ── Top bar ── */}
      <div className={styles.topBar}>
        <div className={styles.left}>

          {/* App menu */}
          <div className={styles.appMenu} ref={menuRef}>
            <button
              className={styles.menuButton}
              onClick={() => setMenuOpen((o) => !o)}
              aria-haspopup="true"
              aria-expanded={menuOpen}
            >
              ☰ <FormattedMessage id="patternStudio.menu.button" defaultMessage="Menu" />
            </button>
            {menuOpen && (
              <div className={styles.menuDropdown} role="menu">
                <button className={styles.menuItem} onClick={() => { handleSave(); setMenuOpen(false); }}>
                  <FormattedMessage id="patternStudio.menu.saveProject" defaultMessage="Save Project" />
                </button>
                <button className={styles.menuItem} onClick={() => { handleExportPDF(); setMenuOpen(false); }}>
                  <FormattedMessage id="patternStudio.menu.exportToPDF" defaultMessage="Export to PDF" />
                </button>
                <button className={styles.menuItem} onClick={() => { setIsGridVisible((v) => !v); setMenuOpen(false); }}>
                  {isGridVisible
                    ? intl.formatMessage({ id: 'patternStudio.menu.hideGrid', defaultMessage: 'Hide Grid' })
                    : intl.formatMessage({ id: 'patternStudio.menu.showGrid', defaultMessage: 'Show Grid' })}
                </button>
                <div className={styles.menuDivider} />
                <button className={`${styles.menuItem} ${styles.disabled}`} disabled>
                  <FormattedMessage id="patternStudio.menu.moreComingSoon" defaultMessage="More (coming soon)" />
                </button>
              </div>
            )}
          </div>

          {/* ── UNDO / REDO buttons ── */}
          <button
            className={styles.toolBtn}
            onClick={undo}
            disabled={!canUndo}
            title={intl.formatMessage({ id: 'patternStudio.toolbar.undo', defaultMessage: 'Undo (Ctrl+Z)' })}
            style={{ opacity: canUndo ? 1 : 0.35, fontSize: 16 }}
            aria-label={intl.formatMessage({ id: 'patternStudio.toolbar.undoLabel', defaultMessage: 'Undo' })}
          >
            ↩
          </button>
          <button
            className={styles.toolBtn}
            onClick={redo}
            disabled={!canRedo}
            title={intl.formatMessage({ id: 'patternStudio.toolbar.redo', defaultMessage: 'Redo (Ctrl+Y)' })}
            style={{ opacity: canRedo ? 1 : 0.35, fontSize: 16 }}
            aria-label={intl.formatMessage({ id: 'patternStudio.toolbar.redoLabel', defaultMessage: 'Redo' })}
          >
            ↪
          </button>
        </div>

        <div className={styles.topControls}>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as UnitType)}
            className={styles.unitSelect}
          >
            <option value="px">{intl.formatMessage({ id: 'patternStudio.unit.px', defaultMessage: 'Pixels' })}</option>
            <option value="cm">{intl.formatMessage({ id: 'patternStudio.unit.cm', defaultMessage: 'cm' })}</option>
            <option value="in">{intl.formatMessage({ id: 'patternStudio.unit.in', defaultMessage: 'Inches' })}</option>
          </select>

          <div className={styles.canvasControls}>
            <label className={styles.canvasLabel}>
              <FormattedMessage id="patternStudio.canvas.label" defaultMessage="Canvas" />
            </label>
            <input
              type="number" min={1} step="0.1"
              title={intl.formatMessage({ id: 'patternStudio.canvas.width', defaultMessage: 'Width' })}
              value={toUnit(paperSize.w).toFixed(1)}
              onChange={(e) => setPaperSize((prev) => ({ ...prev, w: Math.max(10, fromUnit(Number(e.target.value))) }))}
              className={styles.canvasInput}
            />
            <input
              type="number" min={1} step="0.1"
              title={intl.formatMessage({ id: 'patternStudio.canvas.height', defaultMessage: 'Height' })}
              value={toUnit(paperSize.h).toFixed(1)}
              onChange={(e) => setPaperSize((prev) => ({ ...prev, h: Math.max(10, fromUnit(Number(e.target.value))) }))}
              className={styles.canvasInput}
            />
            <div className={styles.unitLabel}>{unit}</div>
          </div>

          <label className={styles.checkboxLabel}>
            <input type="checkbox" checked={snapEnabled} onChange={(e) => setSnapEnabled(e.target.checked)} />
            {intl.formatMessage({ id: 'patternStudio.snapGrid', defaultMessage: 'Snap Grid' })}
          </label>
        </div>
      </div>

      {/* ── Main area ── */}
      <div className={styles.mainArea}>

        {/* Tool bar */}
        <div className={styles.toolbar}>
          <div>
            <button className={`${styles.toolBtn} ${tool === 'select' ? styles.active : ''}`} onClick={() => setTool('select')} title={intl.formatMessage({ id: 'patternStudio.tool.select', defaultMessage: 'Select' })}>↗</button>
            <button className={`${styles.toolBtn} ${tool === 'hand' ? styles.active : ''}`} onClick={() => setTool('hand')} title={intl.formatMessage({ id: 'patternStudio.tool.hand', defaultMessage: 'Hand' })}><FaHandPaper /></button>
            <button className={`${styles.toolBtn} ${tool === 'pen' ? styles.active : ''}`} onClick={() => setTool('pen')} title={intl.formatMessage({ id: 'patternStudio.tool.pen', defaultMessage: 'Pen' })}><FaPenNib /></button>
            <button className={`${styles.toolBtn} ${tool === 'bezier' ? styles.active : ''}`} onClick={() => setTool('bezier')} title={intl.formatMessage({ id: 'patternStudio.tool.bezierCurve', defaultMessage: 'Bezier Curve' })}>⌇</button>
            <button
              className={`${styles.toolBtn} ${tool === 'bezierPen' ? styles.active : ''}`}
              onClick={() => setTool('bezierPen')}
              title={intl.formatMessage({ id: 'patternStudio.tool.bezierPenTitle', defaultMessage: 'Bézier Pen — click to place anchors (Enter to finish, Esc to cancel)' })}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <CiPen size={20} />
            </button>
          </div>
          <div>
            <button className={`${styles.toolBtn} ${tool === 'rect' ? styles.active : ''}`} onClick={() => setTool('rect')} title={intl.formatMessage({ id: 'patternStudio.tool.rectangle', defaultMessage: 'Rectangle' })}><MdCropSquare /></button>
            <button className={`${styles.toolBtn} ${tool === 'circle' ? styles.active : ''}`} onClick={() => setTool('circle')} title={intl.formatMessage({ id: 'patternStudio.tool.circle', defaultMessage: 'Circle' })}><FaRegCircle /></button>
            <button className={`${styles.toolBtn} ${tool === 'line' ? styles.active : ''}`} onClick={() => setTool('line')} title={intl.formatMessage({ id: 'patternStudio.tool.line', defaultMessage: 'Line' })}>📏</button>
            <button className={`${styles.toolBtn} ${tool === 'text' ? styles.active : ''}`} onClick={() => setTool('text')} title={intl.formatMessage({ id: 'patternStudio.tool.text', defaultMessage: 'Text' })}>T</button>
          </div>
        </div>

        <div className={styles.canvasArea}>
          <div className={styles.rulerHorizontal}>
            <Ruler type="h" scale={stageScale} offset={stagePos.x} length={dimensions.w} unit={unit} />
          </div>

          <div className={styles.workArea}>
            <div className={styles.rulerVertical}>
              <Ruler type="v" scale={stageScale} offset={stagePos.y} length={dimensions.h} unit={unit} />
            </div>

            <div className={styles.canvasScroll} ref={stageContainerRef}>
              <Stage
                ref={stageRef}
                width={dimensions.w - 30}
                height={dimensions.h - 30}
                className={`${tool === 'hand' ? styles.grabbing : ''} ${styles.stage}`}
                draggable={tool === 'hand'}
                scaleX={stageScale}
                scaleY={stageScale}
                x={stagePos.x}
                y={stagePos.y}
                onWheel={handleWheel}
                onDragEnd={handleStageDragEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onDblClick={(e) => { if (tool === 'bezierPen') bezierPenDblClick(e, stageRef.current); }}
              >
                <Layer ref={layerRef}>

                  {/* Paper background */}
                  <Rect
                    x={0} y={0} width={paperSize.w} height={paperSize.h}
                    fill="white" shadowColor="black" shadowBlur={10} shadowOpacity={0.2}
                    listening={false}
                  />

                  {/* Grid */}
                  {isGridVisible && (
                    <Group>
                      {gridLines.v.map((x) => (
                        <Line key={`gv${x}`} points={[x, -50000, x, 50000]} stroke="#f0f0f0" strokeWidth={1 / stageScale} listening={false} />
                      ))}
                      {gridLines.h.map((y) => (
                        <Line key={`gh${y}`} points={[-50000, y, 50000, y]} stroke="#f0f0f0" strokeWidth={1 / stageScale} listening={false} />
                      ))}
                    </Group>
                  )}

                  {/* Shapes */}
                  {shapes.map((shape) => {
                    const isSelected = selectedId === shape.id;
                    const isHovered = hoveredId === shape.id;

                    // Shared event props for draggable shapes
                    const dragProps = {
                      draggable: tool === 'select' && shape.type !== 'bezier',
                      onClick: () => { if (tool === 'select') setSelectedId(shape.id); },
                      onTap: () => { if (tool === 'select') setSelectedId(shape.id); },
                      onMouseEnter: () => setHoveredId(shape.id),
                      onMouseLeave: () => setHoveredId((prev) => (prev === shape.id ? null : prev)),
                      onDragMove: (e: Konva.KonvaEventObject<DragEvent>) => {
                        if (shape.type === 'bezier') return;
                        setDraggingShapeOffset({ id: shape.id, x: e.target.x(), y: e.target.y() });
                      },
                      onDragEnd: (e: Konva.KonvaEventObject<DragEvent>) => {
                        if (shape.type === 'bezier') return;
                        const updated: Shape = { ...shape, x: e.target.x(), y: e.target.y() };
                        const next = shapes.map((s) => (s.id === shape.id ? updated : s));
                        setShapes(next);
                        safeCommit(next, bezierShapes);
                        setDraggingShapeOffset((prev) => (prev?.id === shape.id ? null : prev));
                      },
                      onTransformEnd: (e: Konva.KonvaEventObject<Event>) => {
                        if (shape.type === 'bezier') return;
                        const node = e.target as Konva.Node;
                        const scaleX = (node as Konva.Rect).scaleX();
                        const scaleY = (node as Konva.Rect).scaleY();
                        (node as Konva.Rect).scaleX(1);
                        (node as Konva.Rect).scaleY(1);
                        let updated: Shape;
                        if (shape.type === 'circle') {
                          updated = {
                            ...shape,
                            x: node.x(), y: node.y(),
                            radius: Math.max(5, Math.max((node as Konva.Circle).width() * scaleX, (node as Konva.Circle).height() * scaleY) / 2),
                            rotation: node.rotation(),
                          };
                        } else {
                          updated = {
                            ...shape,
                            x: node.x(), y: node.y(),
                            width: Math.max(5, (node as Konva.Rect).width() * scaleX),
                            height: Math.max(5, (node as Konva.Rect).height() * scaleY),
                            rotation: node.rotation(),
                          };
                        }
                        console.log('Transformed shape:', updated);
                        const next = shapes.map((s) => (s.id === shape.id ? updated : s));
                        setShapes(next);
                        safeCommit(next, bezierShapes);
                      },
                    };

                    // ── rect ──────────────────────────────────────────────
                    if (shape.type === 'rect') {
                      const bounds = getShapeBounds(shape);
                      const renderFill = shape.fill && shape.fill !== 'transparent' ? shape.fill : 'rgba(0,0,0,0.001)';
                      return (
                        <Group key={shape.id}>
                          <Rect
                            {...dragProps}
                            id={shape.id}
                            x={shape.x} y={shape.y}
                            width={shape.width} height={shape.height}
                            fill={renderFill} stroke={shape.stroke} strokeWidth={shape.strokeWidth}
                          />
                          {(isSelected || isHovered) && (
                            <KonvaText
                              x={bounds.x + bounds.w / 2 - 40 / stageScale} width={80 / stageScale}
                              y={bounds.y - 20 / stageScale}
                              text={formatMeasurement(bounds.w)} align="center"
                              fontSize={Math.max(10, 12 / stageScale)} listening={false}
                            />
                          )}
                          {(isSelected || isHovered) && (
                            <KonvaText
                              x={bounds.x + bounds.w + 8 / stageScale}
                              y={bounds.y + bounds.h / 2 - 10 / stageScale}
                              text={formatMeasurement(bounds.h)} rotation={90}
                              width={80 / stageScale} align="center"
                              fontSize={Math.max(10, 12 / stageScale)} listening={false}
                            />
                          )}
                        </Group>
                      );
                    }

                    // ── circle ────────────────────────────────────────────
                    if (shape.type === 'circle') {
                      const bounds = getShapeBounds(shape);
                      const renderFill = shape.fill && shape.fill !== 'transparent' ? shape.fill : 'rgba(0,0,0,0.001)';
                      return (
                        <Group key={shape.id}>
                          <Circle
                            {...dragProps}
                            id={shape.id}
                            x={shape.x} y={shape.y} radius={shape.radius}
                            fill={renderFill} stroke={shape.stroke} strokeWidth={shape.strokeWidth}
                          />
                          {(isSelected || isHovered) && (
                            <KonvaText
                              x={bounds.x + bounds.w / 2 - 40 / stageScale} width={80 / stageScale}
                              y={bounds.y + bounds.h + 6 / stageScale}
                              text={formatMeasurement(bounds.w)} align="center"
                              fontSize={Math.max(10, 12 / stageScale)} listening={false}
                            />
                          )}
                        </Group>
                      );
                    }

                    // ── text ──────────────────────────────────────────────
                    if (shape.type === 'text') {
                      return (
                        <Group key={shape.id}>
                          <KonvaText
                            {...dragProps}
                            id={shape.id}
                            x={shape.x} y={shape.y}
                            text={shape.text} fontSize={shape.fontSize} fill={shape.fill}
                          />
                        </Group>
                      );
                    }

                    // ── line / pen ────────────────────────────────────────
                    if (shape.type === 'line' || shape.type === 'pen') {
                      const pts = shape.points ?? [];
                      const len = getShapeLength(shape);
                      let mx = 0, my = 0, angle = 0;
                      if (pts.length >= 4) {
                        mx = (pts[0] + pts[2]) / 2;
                        my = (pts[1] + pts[3]) / 2;
                        angle = Math.atan2(pts[3] - pts[1], pts[2] - pts[0]);
                      } else if (pts.length >= 2) {
                        mx = pts[0]; my = pts[1];
                      }
                      const ox = -Math.sin(angle) * 12;
                      const oy = Math.cos(angle) * 12;
                      return (
                        <Group key={shape.id}>
                          <Line
                            {...dragProps}
                            id={shape.id}
                            points={pts}
                            closed={shape.type === 'pen' ? Boolean(shape.closed) : false}
                            stroke={shape.stroke} strokeWidth={shape.strokeWidth}
                            tension={shape.type === 'pen' ? (shape.tension ?? 0.5) : 0}
                            lineCap="round" lineJoin="round"
                            fill={shape.fill ?? 'transparent'}
                          />
                          {(isSelected || isHovered) && (
                            <KonvaText
                              x={mx + ox - 60 / stageScale} width={120 / stageScale}
                              y={my + oy - 8 / stageScale}
                              text={formatMeasurement(len)} align="center"
                              fontSize={Math.max(10, 12 / stageScale)} listening={false}
                            />
                          )}
                        </Group>
                      );
                    }

                    // ── quad ──────────────────────────────────────────────
                    if (shape.type === 'quad') {
                      const pts = shape.points ?? [0, 0, 0, 0, 0, 0, 0, 0];
                      return (
                        <Group
                          key={shape.id}
                          id={shape.id}
                          draggable={tool === 'select'}
                          onClick={() => { if (tool === 'select') { setSelectedId(shape.id); setSelectedVertexIndex(null); } }}
                          onDragMove={(e) => setDraggingShapeOffset({ id: shape.id, x: e.target.x(), y: e.target.y() })}
                          onDragEnd={(e) => {
                            const dx = e.target.x();
                            const dy = e.target.y();
                            if (dx !== 0 || dy !== 0) {
                              const movedPts = (shape.points ?? []).map((v, i) => i % 2 === 0 ? v + dx : v + dy);
                              const next = shapes.map((s) =>
                                s.id === shape.id ? { ...s, points: movedPts } : s,
                              );
                              setShapes(next);
                              safeCommit(next, bezierShapes);
                              e.target.position({ x: 0, y: 0 });
                            }
                            setDraggingShapeOffset((prev) => (prev?.id === shape.id ? null : prev));
                          }}
                          onTransformStart={() => {
                            // snapshot points BEFORE the transform begins
                            transformBeforeRef.current = (shape.points ?? []).slice() as any;
                          }}

                          onTransformEnd={(e) => {
                            const node = e.target;
                            didModifyRef.current = true;

                            const scaleX = node.scaleX();
                            const scaleY = node.scaleY();
                            const rotation = node.rotation(); // degrees
                            const dx = node.x();
                            const dy = node.y();

                            node.setAttrs({ x: 0, y: 0, scaleX: 1, scaleY: 1, rotation: 0 });

                            // Use the pre-transform snapshot, not the current (already-drifted) shape.points
                            const original: number[] = (transformBeforeRef.current as any) ?? (shape.points ?? []).slice();

                            const rad = (rotation * Math.PI) / 180;
                            const cos = Math.cos(rad);
                            const sin = Math.sin(rad);

                            const ptsCopy = original.slice();
                            for (let i = 0; i < ptsCopy.length; i += 2) {
                              const px = ptsCopy[i] * scaleX;
                              const py = ptsCopy[i + 1] * scaleY;
                              // rotate around origin (group sits at 0,0 normally)
                              ptsCopy[i] = px * cos - py * sin + dx;
                              ptsCopy[i + 1] = px * sin + py * cos + dy;
                            }

                            const next = shapes.map((s) =>
                              s.id === shape.id ? { ...s, points: ptsCopy } : s,
                            );
                            setShapes(next);
                            safeCommit(next, bezierShapes);
                            transformBeforeRef.current = null;
                            setDraggingShapeOffset((prev) => (prev?.id === shape.id ? null : prev));
                          }}
                        >
                          <Line
                            points={pts} closed
                            stroke={shape.stroke} strokeWidth={shape.strokeWidth}
                            fill={shape.fill ?? 'transparent'} lineJoin="round" lineCap="round"
                          />
                        </Group>
                      );
                    }

                    // ── bezier ────────────────────────────────────────────
                    if (shape.type === 'bezier') {
                      const pts = shape.points ?? [0, 0, 0, 0, 0, 0, 0, 0];
                      const bounds = getShapeBounds(shape);
                      const len = getShapeLength(shape);
                      return (
                        <Group
                          key={shape.id}
                          id={shape.id}
                          x={shape.x} y={shape.y}
                          draggable={tool === 'select'}
                          onClick={() => { if (tool === 'select') setSelectedId(shape.id); }}
                          onMouseEnter={() => setHoveredId(shape.id)}
                          onMouseLeave={() => setHoveredId((prev) => (prev === shape.id ? null : prev))}
                          onDragEnd={(e) => {
                            if (e.target.id() !== shape.id) return;
                            let x = e.target.x();
                            let y = e.target.y();
                            if (snapEnabled) { x = snapToGrid(x); y = snapToGrid(y); e.target.position({ x, y }); }
                            const next = shapes.map((s) => (s.id === shape.id ? { ...s, x, y } : s));
                            setShapes(next);
                            safeCommit(next, bezierShapes);
                          }}
                        >
                          <Line
                            points={pts}
                            stroke={shape.stroke} strokeWidth={shape.strokeWidth}
                            bezier lineCap={shape.lineCap ?? 'round'} lineJoin={shape.lineJoin ?? 'round'}
                          />
                          {(isSelected || isHovered) && (
                            <KonvaText
                              x={bounds.x + bounds.w / 2 - 40 / stageScale} width={80 / stageScale}
                              y={bounds.y + bounds.h / 2 - 8 / stageScale}
                              text={formatMeasurement(len)} align="center"
                              fontSize={Math.max(10, 12 / stageScale)} listening={false}
                            />
                          )}
                          {isSelected && (
                            <>
                              <Line points={[pts[0], pts[1], pts[2], pts[3]]} stroke="#999" strokeWidth={1} dash={[4, 4]} />
                              <Line points={[pts[6], pts[7], pts[4], pts[5]]} stroke="#999" strokeWidth={1} dash={[4, 4]} />
                              {([0, 1, 2, 3] as const).map((pi) => {
                                const isAnchor = pi === 0 || pi === 3;
                                return (
                                  <Circle
                                    key={pi}
                                    x={pts[pi * 2]} y={pts[pi * 2 + 1]}
                                    radius={isAnchor ? 4 : 3}
                                    fill={isAnchor ? 'white' : '#ffcc00'}
                                    stroke="#666"
                                    draggable
                                    onDragMove={(e) => handlePointDragMove(e, pi, shape.id)}
                                    onDragEnd={(e) => {
                                      handlePointDragMove(e, pi, shape.id);
                                      flushPendingDrag(shape.id);
                                      safeCommit(shapes, bezierShapes);
                                    }}
                                  />
                                );
                              })}
                            </>
                          )}
                        </Group>
                      );
                    }

                    return null;
                  })}

                  <Transformer ref={transformerRef} />
                </Layer>

                {/* ── Overlay layer: vertex / edge handles ── */}
                <Layer ref={overlayRef} listening>
                  {selectedShape &&
                    (selectedShape.type === 'rect' ||
                      selectedShape.type === 'quad' ||
                      selectedShape.type === 'pen') &&
                    (() => {
                      const r = Math.max(5, 8 / stageScale);
                      const handleDist = 18 / stageScale;

                      const dragOffset: { x: number; y: number } =
                        draggingShapeOffset?.id === selectedShape.id
                          ? selectedShape.type === 'quad'
                            ? { x: draggingShapeOffset.x, y: draggingShapeOffset.y }
                            : {
                              x: draggingShapeOffset.x - (selectedShape.x ?? 0),
                              y: draggingShapeOffset.y - (selectedShape.y ?? 0),
                            }
                          : { x: 0, y: 0 };

                      // ── rect handles ────────────────────────────────────
                      if (selectedShape.type === 'rect') {
                        const bounds = getShapeBounds(selectedShape);
                        const cx = bounds.x + bounds.w / 2;
                        const cy = bounds.y + bounds.h / 2;

                        const corners: Array<{ x: number; y: number; idx: number }> = [
                          { x: bounds.x - handleDist + dragOffset.x, y: bounds.y - handleDist + dragOffset.y, idx: 0 },
                          { x: bounds.x + bounds.w + handleDist + dragOffset.x, y: bounds.y - handleDist + dragOffset.y, idx: 1 },
                          { x: bounds.x + bounds.w + handleDist + dragOffset.x, y: bounds.y + bounds.h + handleDist + dragOffset.y, idx: 2 },
                          { x: bounds.x - handleDist + dragOffset.x, y: bounds.y + bounds.h + handleDist + dragOffset.y, idx: 3 },
                        ];
                        const edges: Array<{ x: number; y: number; idx: number }> = [
                          { x: cx + dragOffset.x, y: bounds.y - handleDist + dragOffset.y, idx: 0 },
                          { x: bounds.x + bounds.w + handleDist + dragOffset.x, y: cy + dragOffset.y, idx: 1 },
                          { x: cx + dragOffset.x, y: bounds.y + bounds.h + handleDist + dragOffset.y, idx: 2 },
                          { x: bounds.x - handleDist + dragOffset.x, y: cy + dragOffset.y, idx: 3 },
                        ];

                        return (
                          <>
                            {corners.filter((c) => Number.isFinite(c.x) && Number.isFinite(c.y)).map((c) => {
                              const active = selectedHandle?.shapeId === selectedShape.id && selectedHandle.kind === 'corner' && selectedHandle.index === c.idx;
                              const dragging = draggingOverlayHandle?.shapeId === selectedShape.id && draggingOverlayHandle.kind === 'corner' && draggingOverlayHandle.index === c.idx;
                              return (
                                <Circle
                                  key={`rect-corner-${c.idx}`}
                                  {...(active ? {} : { x: c.x, y: c.y })}
                                  radius={r} fill="#fff" stroke={active ? '#1976d2' : '#333'}
                                  draggable opacity={dragging ? 0 : 1}
                                  onDragStart={() => setDraggingOverlayHandle({ shapeId: selectedShape.id, kind: 'corner', index: c.idx })}
                                  onMouseDown={(e) => { e.evt.stopPropagation(); convertRectToQuad(selectedShape.id); setSelectedId(selectedShape.id); setSelectedHandle({ shapeId: selectedShape.id, kind: 'corner', index: c.idx }); }}
                                  onDragMove={(e) => handleRectHandleDragMove(e, 'corner', c.idx, selectedShape.id)}
                                  onDragEnd={(e) => { handleRectHandleDragMove(e, 'corner', c.idx, selectedShape.id); flushPendingDrag(selectedShape.id); safeCommit(shapes, bezierShapes); setSelectedHandle(null); setDraggingOverlayHandle(null); }}
                                />
                              );
                            })}
                            {edges.filter((ed) => Number.isFinite(ed.x) && Number.isFinite(ed.y)).map((ed) => {
                              const active = selectedHandle?.shapeId === selectedShape.id && selectedHandle.kind === 'edge' && selectedHandle.index === ed.idx;
                              const dragging = draggingOverlayHandle?.shapeId === selectedShape.id && draggingOverlayHandle.kind === 'edge' && draggingOverlayHandle.index === ed.idx;
                              return (
                                <Circle
                                  key={`rect-edge-${ed.idx}`}
                                  {...(active ? {} : { x: ed.x, y: ed.y })}
                                  radius={r} fill="#fff" stroke={active ? '#1976d2' : '#666'}
                                  draggable opacity={dragging ? 0 : 1}
                                  onDragStart={() => setDraggingOverlayHandle({ shapeId: selectedShape.id, kind: 'edge', index: ed.idx })}
                                  onMouseDown={(e) => { e.evt.stopPropagation(); setSelectedId(selectedShape.id); setSelectedHandle({ shapeId: selectedShape.id, kind: 'edge', index: ed.idx }); }}
                                  onDblClick={(e) => {
                                    e.evt.stopPropagation();
                                    const stage = e.target.getStage();
                                    const pointer = stage?.getPointerPosition();
                                    if (!stage || !pointer) return;
                                    const t = stage.getAbsoluteTransform().copy();
                                    t.invert();
                                    insertPointOnEdge(selectedShape.id, ed.idx, t.point(pointer));
                                  }}
                                  onDragMove={(e) => handleRectHandleDragMove(e, 'edge', ed.idx, selectedShape.id)}
                                  onDragEnd={(e) => { handleRectHandleDragMove(e, 'edge', ed.idx, selectedShape.id); flushPendingDrag(selectedShape.id); safeCommit(shapes, bezierShapes); setSelectedHandle(null); setDraggingOverlayHandle(null); }}
                                />
                              );
                            })}
                          </>
                        );
                      }

                      // ── quad / pen handles ──────────────────────────────
                      const pts = selectedShape.points ?? [];
                      const n = Math.max(0, Math.floor(pts.length / 2));

                      const cornerHandles = Array.from({ length: n }, (_, i) => ({
                        x: pts[i * 2] + dragOffset.x,
                        y: pts[i * 2 + 1] + dragOffset.y,
                        idx: i,
                      }));
                      const edgeHandles = Array.from({ length: n }, (_, i) => {
                        const a = i;
                        const b = (i + 1) % n;
                        return {
                          x: (pts[a * 2] + pts[b * 2]) / 2 + dragOffset.x,
                          y: (pts[a * 2 + 1] + pts[b * 2 + 1]) / 2 + dragOffset.y,
                          idx: i,
                        };
                      });

                      return (
                        <>
                          {cornerHandles.filter((h) => Number.isFinite(h.x) && Number.isFinite(h.y)).map((h) => {
                            const active = selectedHandle?.shapeId === selectedShape.id && selectedHandle.kind === 'corner' && selectedHandle.index === h.idx;
                            const dragging = draggingOverlayHandle?.shapeId === selectedShape.id && draggingOverlayHandle.kind === 'corner' && draggingOverlayHandle.index === h.idx;
                            return (
                              <Circle
                                key={`poly-corner-${h.idx}`}
                                {...(active ? {} : { x: h.x, y: h.y })}
                                radius={r} fill="#fff" stroke={active ? '#1976d2' : '#333'}
                                draggable opacity={dragging ? 0 : 1}
                                onDragStart={() => setDraggingOverlayHandle({ shapeId: selectedShape.id, kind: 'corner', index: h.idx })}
                                onMouseEnter={() => setHoveredHandle({ shapeId: selectedShape.id, kind: 'corner', index: h.idx })}
                                onMouseLeave={() => setHoveredHandle(null)}
                                onMouseDown={(e) => { e.evt.stopPropagation(); setSelectedId(selectedShape.id); setSelectedHandle({ shapeId: selectedShape.id, kind: 'corner', index: h.idx }); setSelectedVertexIndex(h.idx); }}
                                onDragMove={(e) => handlePointDragMove(e, h.idx, selectedShape.id)}
                                onDragEnd={(e) => { handlePointDragMove(e, h.idx, selectedShape.id); flushPendingDrag(selectedShape.id); safeCommit(shapes, bezierShapes); setSelectedHandle(null); setDraggingOverlayHandle(null); }}
                              />
                            );
                          })}
                          {edgeHandles.filter((h) => Number.isFinite(h.x) && Number.isFinite(h.y)).map((h) => {
                            const active = selectedHandle?.shapeId === selectedShape.id && selectedHandle.kind === 'edge' && selectedHandle.index === h.idx;
                            const dragging = draggingOverlayHandle?.shapeId === selectedShape.id && draggingOverlayHandle.kind === 'edge' && draggingOverlayHandle.index === h.idx;
                            return (
                              <Circle
                                key={`poly-edge-${h.idx}`}
                                {...(active ? {} : { x: h.x, y: h.y })}
                                radius={Math.max(5, 7 / stageScale)} fill="#fff" stroke={active ? '#1976d2' : '#666'}
                                draggable opacity={dragging ? 0 : 1}
                                onDragStart={() => setDraggingOverlayHandle({ shapeId: selectedShape.id, kind: 'edge', index: h.idx })}
                                onMouseDown={(e) => { e.evt.stopPropagation(); setSelectedId(selectedShape.id); setSelectedHandle({ shapeId: selectedShape.id, kind: 'edge', index: h.idx }); }}
                                onDblClick={(e) => {
                                  e.evt.stopPropagation();
                                  const stage = e.target.getStage();
                                  const pointer = stage?.getPointerPosition();
                                  if (!stage || !pointer) return;
                                  const t = stage.getAbsoluteTransform().copy().invert();
                                  insertPointOnEdge(selectedShape.id, h.idx, t.point(pointer));
                                }}
                                onDragMove={(e) => handlePolygonEdgeDragMove(e, h.idx, selectedShape.id)}
                                onDragEnd={(e) => { handlePolygonEdgeDragMove(e, h.idx, selectedShape.id); flushPendingDrag(selectedShape.id); safeCommit(shapes, bezierShapes); setSelectedHandle(null); setDraggingOverlayHandle(null); }}
                              />
                            );
                          })}
                        </>
                      );
                    })()}
                </Layer>

                {/* ── BezierPen layer ── */}
                <BezierPenLayer
                  shapes={bezierShapes}
                  selectedId={selectedBezierPenId}
                  stageScale={stageScale}
                  tool={tool}
                  onSelect={(bId: string) => { setSelectedBezierPenId(bId); setSelectedId(null); }}
                  onShapeUpdate={(shape: BPS) => {
                    setBezierShapes((prev) => {
                      const idx = prev.findIndex((s) => s.id === shape.id);
                      if (idx === -1) return prev;
                      const next = prev.slice();
                      next[idx] = shape;
                      return next;
                    });
                  }}
                  onShapeDragEnd={(shape: BPS) => {
                    setBezierShapes((prev) => {
                      const idx = prev.findIndex((s) => s.id === shape.id);
                      if (idx === -1) return prev;
                      const next = prev.slice();
                      next[idx] = shape;
                      safeCommit(shapes, next);
                      return next;
                    });
                  }}
                  formatMeasurement={formatMeasurement}
                />
              </Stage>
            </div>
          </div>
        </div>

        {/* ── Properties panel ── */}
        <div className={styles.propertiesPanel}>
          <h3>
            <FormattedMessage id="patternStudio.properties.title" defaultMessage="Properties" />
          </h3>

          {selectedShape ? (
            <>
              <div className={styles.section}>
                <label>
                  <FormattedMessage id="patternStudio.properties.type" defaultMessage="Type" />: {selectedShape.type}
                </label>
                {(selectedShape.type === 'rect' || selectedShape.type === 'circle' || selectedShape.type === 'text') && (
                  <div className={styles.row}>
                    <div className={styles.col}>
                      <label>
                        <FormattedMessage id="patternStudio.properties.x" defaultMessage="X" /> ({unit})
                      </label>
                      <input type="number" value={toUnit(selectedShape.x).toFixed(1)} onChange={(e) => updateSelectedProperty('x', fromUnit(Number(e.target.value)))} />
                    </div>
                    <div className={styles.col}>
                      <label>
                        <FormattedMessage id="patternStudio.properties.y" defaultMessage="Y" /> ({unit})
                      </label>
                      <input type="number" value={toUnit(selectedShape.y).toFixed(1)} onChange={(e) => updateSelectedProperty('y', fromUnit(Number(e.target.value)))} />
                    </div>
                  </div>
                )}
              </div>

              {selectedShape.type === 'rect' && (
                <div className={styles.row}>
                  <div className={styles.col}>
                    <label>
                      <FormattedMessage id="patternStudio.properties.width" defaultMessage="Width" /> ({unit})
                    </label>
                    <input type="number" value={toUnit(selectedShape.width ?? 0).toFixed(1)} onChange={(e) => updateSelectedProperty('width', fromUnit(Number(e.target.value)))} />
                  </div>
                  <div className={styles.col}>
                    <label>
                      <FormattedMessage id="patternStudio.properties.height" defaultMessage="Height" /> ({unit})
                    </label>
                    <input type="number" value={toUnit(selectedShape.height ?? 0).toFixed(1)} onChange={(e) => updateSelectedProperty('height', fromUnit(Number(e.target.value)))} />
                  </div>
                </div>
              )}

              {selectedShape.type === 'circle' && (
                <div className={styles.section}>
                  <label>
                    <FormattedMessage id="patternStudio.properties.radius" defaultMessage="Radius" /> ({unit})
                  </label>
                  <input type="number" value={toUnit(selectedShape.radius ?? 0).toFixed(1)} onChange={(e) => updateSelectedProperty('radius', fromUnit(Number(e.target.value)))} />
                </div>
              )}

              {selectedShape.type === 'line' && (selectedShape.points?.length ?? 0) === 4 && (
                <div className={styles.section}>
                  <label>
                    <FormattedMessage id="patternStudio.properties.length" defaultMessage="Length" /> ({unit})
                  </label>
                  <input
                    type="number"
                    value={toUnit(
                      Math.sqrt(
                        Math.pow((selectedShape.points![2] - selectedShape.points![0]), 2) +
                        Math.pow((selectedShape.points![3] - selectedShape.points![1]), 2),
                      ),
                    ).toFixed(1)}
                    onChange={(e) => updateLineLength(Number(e.target.value))}
                  />
                </div>
              )}

              {selectedShape.type === 'pen' && (
                <div className={styles.section}>
                  <label>
                    <FormattedMessage id="patternStudio.properties.smoothing" defaultMessage="Smoothing" />
                  </label>
                  <input
                    type="range" min="0" max="1" step="0.1"
                    value={selectedShape.tension ?? 0.5}
                    onChange={(e) => updateSelectedProperty('tension', parseFloat(e.target.value))}
                  />
                </div>
              )}

              <div className={styles.section}>
                <label>
                  <FormattedMessage id="patternStudio.properties.strokeColor" defaultMessage="Stroke Color" />
                </label>
                <div className={styles.colorPicker}>
                  <input type="color" value={selectedShape.stroke ?? '#000000'} onChange={(e) => updateSelectedProperty('stroke', e.target.value)} />
                </div>
              </div>

              <div className={styles.section}>
                <label>
                  <FormattedMessage id="patternStudio.properties.strokeWidth" defaultMessage="Stroke Width" />
                </label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input type="range" min="0" max="60" step="0.5" value={selectedShape.strokeWidth ?? 1} onChange={(e) => updateSelectedProperty('strokeWidth', Number(e.target.value))} className={styles.slider} />
                  <input type="number" min="0" step="0.5" value={selectedShape.strokeWidth ?? 1} onChange={(e) => updateSelectedProperty('strokeWidth', Number(e.target.value))} style={{ width: 60 }} />
                </div>
              </div>

              <div className={styles.section}>
                <button onClick={deleteSelected} className={styles.deleteBtn}>
                  <FormattedMessage id="patternStudio.properties.delete" defaultMessage="Delete" />
                </button>
              </div>
            </>
          ) : selectedBezierPenId
            ? (() => {
              const bShape = bezierShapes.find((s: any) => s.id === selectedBezierPenId);
              if (!bShape) return (
                <div className={styles.emptyState}>
                  <p>{intl.formatMessage({ id: 'patternStudio.emptyState.zoomLabel', defaultMessage: 'Zoom' })}: {Math.round(stageScale * 100)}%</p>
                </div>
              );
              return (
                <div>
                  <div className={styles.section}>
                    <label>
                      <FormattedMessage id="patternStudio.bezier.type" defaultMessage="Type: Bézier Pen Path" />
                    </label>
                    <label style={{ display: 'block', marginTop: 4, opacity: 0.7, fontSize: 12 }}>
                      <FormattedMessage
                        id="patternStudio.bezier.anchors"
                        defaultMessage="Anchors: {count}"
                        values={{ count: bShape.anchors.length }}
                      />
                    </label>
                  </div>

                  <div className={styles.section}>
                    <label>
                      <FormattedMessage id="patternStudio.properties.strokeColor" defaultMessage="Stroke Color" />
                    </label>
                    <div className={styles.colorPicker}>
                      <input
                        type="color"
                        value={bShape.stroke ?? '#000000'}
                        onChange={(e) => {
                          const next = bezierShapes.map((s: any) => s.id === bShape.id ? { ...s, stroke: e.target.value } : s);
                          setBezierShapes(next);
                          safeCommit(shapes, next);
                        }}
                      />
                    </div>
                  </div>

                  <div className={styles.section}>
                    <label>
                      <FormattedMessage id="patternStudio.properties.strokeWidth" defaultMessage="Stroke Width" />
                    </label>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="range" min="0" max="60" step="0.5"
                        value={bShape.strokeWidth ?? 2}
                        onChange={(e) => {
                          const next = bezierShapes.map((s: any) => s.id === bShape.id ? { ...s, strokeWidth: Number(e.target.value) } : s);
                          setBezierShapes(next);
                          safeCommit(shapes, next);
                        }}
                        className={styles.slider}
                      />
                      <input
                        type="number" min="0" step="0.5"
                        value={bShape.strokeWidth ?? 2}
                        onChange={(e) => {
                          const next = bezierShapes.map((s: any) => s.id === bShape.id ? { ...s, strokeWidth: Number(e.target.value) } : s);
                          setBezierShapes(next);
                          safeCommit(shapes, next);
                        }}
                        style={{ width: 60 }}
                      />
                    </div>
                  </div>

                  <div className={styles.section}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={bShape.closed}
                        onChange={(e) => {
                          const next = bezierShapes.map((s: any) => s.id === bShape.id ? { ...s, closed: e.target.checked } : s);
                          setBezierShapes(next);
                          safeCommit(shapes, next);
                        }}
                      />
                      <FormattedMessage id="patternStudio.bezier.closePath" defaultMessage="Close Path" />
                    </label>
                  </div>

                  <div className={styles.section}>
                    <button
                      onClick={() => {
                        const next = bezierShapes.filter((s: any) => s.id !== selectedBezierPenId);
                        setBezierShapes(next);
                        safeCommit(shapes, next);
                        setSelectedBezierPenId(null);
                      }}
                      className={styles.deleteBtn}
                    >
                      <FormattedMessage id="patternStudio.properties.delete" defaultMessage="Delete" />
                    </button>
                  </div>
                </div>
              );
            })()
            : (
              <div className={styles.emptyState}>
                <p>
                  {intl.formatMessage({ id: 'patternStudio.emptyState.zoomLabel', defaultMessage: 'Zoom' })}: {Math.round(stageScale * 100)}%
                </p>
                <p>
                  {intl.formatMessage({ id: 'patternStudio.emptyState.gridLabel', defaultMessage: 'Grid' })}: {unit}
                </p>
              </div>
            )
          }

          <h3>
            <FormattedMessage id="patternStudio.layers.title" defaultMessage="Layers" />
          </h3>
          <div className={styles.layersList} role="list">
            {shapes.map((s: any, i: any) => (
              <div
                key={s.id}
                role="listitem"
                draggable
                aria-grabbed={draggingIndex === i}
                className={[
                  styles.layerItem,
                  selectedId === s.id ? styles.selected : '',
                  draggingIndex === i ? styles.dragging : '',
                  dragOverIndex === i ? styles.dragOver : '',
                ].join(' ')}
                onClick={() => setSelectedId(s.id)}
                onDragStart={(e) => onLayerDragStart(e, i)}
                onDragOver={(e) => onLayerDragOver(e, i)}
                onDrop={(e) => onLayerDrop(e, i)}
                onDragEnd={onLayerDragEnd}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ textTransform: 'capitalize' }}>{s.type} #{i}</div>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>
                    {s.id === selectedId ? intl.formatMessage({ id: 'patternStudio.layers.selected', defaultMessage: 'selected' }) : ''}
                  </div>
                </div>
              </div>
            ))}

            {bezierShapes.map((s: any, i: any) => (
              <div
                key={s.id}
                role="listitem"
                className={`${styles.layerItem} ${selectedBezierPenId === s.id ? styles.selected : ''}`}
                onClick={() => { setSelectedBezierPenId(s.id); setSelectedId(null); }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <CiPen size={12} style={{ flexShrink: 0 }} />
                    <span>
                      {intl.formatMessage(
                        { id: 'patternStudio.layers.bezierLabel', defaultMessage: 'Bézier #{index}' },
                        { index: i }
                      )}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, opacity: 0.6 }}>
                    {selectedBezierPenId === s.id ? intl.formatMessage({ id: 'patternStudio.layers.selected', defaultMessage: 'selected' }) : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternStudio;